import type { ReactElement, ReactNode } from 'react';
import { Plus } from 'lucide-react';

import { useFilters } from '../state/FiltersProvider';
import { Button, EmptyState } from './ui';

export function PageHeader({
  title,
  sub,
  actions,
}: {
  title: string;
  sub?: ReactNode;
  actions?: ReactNode;
}): ReactElement {
  return (
    <div className="page-header">
      <div>
        <h1>{title}</h1>
        {sub ? <p className="page-header__sub">{sub}</p> : null}
      </div>
      {actions ? <div className="page-header__actions">{actions}</div> : null}
    </div>
  );
}

/**
 * F-16: the creation button is an icon-only «+» with a real name — no text, no
 * decoration. One per view (STYLEGUIDE: one primary action).
 */
export function CreateButton({ label, onClick }: { label: string; onClick: () => void }): ReactElement {
  return (
    <button type="button" className="icon-btn icon-btn--primary" aria-label={label} title={label} onClick={onClick}>
      <Plus size={16} aria-hidden="true" />
    </button>
  );
}

/** The two empty states must be distinguishable (STYLEGUIDE §6). */
export function FilteredEmptyState({
  what = 'проекты',
  whatGenitive = 'проектов',
}: {
  /** Nominative plural — «Фильтры исключили все …». */
  what?: string;
  /** Genitive plural — «Пока нет …». */
  whatGenitive?: string;
}): ReactElement {
  const { clear, isActive } = useFilters();
  if (!isActive) {
    return <EmptyState title={`Пока нет ${whatGenitive}`} hint="Данные появятся после загрузки реестра." />;
  }
  return (
    <EmptyState
      title={`Фильтры исключили все ${what}`}
      hint="Вид не содержит строк, удовлетворяющих текущим фильтрам."
      action={
        <Button size="sm" variant="secondary" onClick={clear}>
          Сбросить фильтры
        </Button>
      }
    />
  );
}
